/**
 * Grid integration for the formula language (`formulaLanguage.ts`): turns the stored formulas of
 * a grid into the effective column list, resolves references against the grid's real columns,
 * and keeps the dependency graph honest. See docs/Grid/FORMULAS.md.
 *
 * Everything here is pure and synchronous — persistence lives in `gridFormulas.ts`.
 *
 * Both kinds of formula are keyed by the same thing, the column's `field`: a calculated column
 * uses `calc:${id}`, an override uses the base column's own field. That is what lets one
 * dependency graph, one resolver and one memo serve both cases — and it means a reference to an
 * overridden column sees the FORMULA's value, not the raw cell underneath.
 *
 * Three things here are load-bearing and easy to undo by accident:
 *
 *   1. The `valueGetter` returns PRIMITIVES, never the tagged union. `compareValues` in the Grid
 *      sorts numbers as numbers and everything else with `localeCompare`, and filtering, search
 *      and copy/paste all go through `cellText`. A getter returning an object would make every
 *      cell `[object Object]` in the sort and in the filter menu.
 *
 *   2. Evaluation is lazy recursion guarded by an `inProgress` marker, not a topological walk.
 *      The marker is the safety net: if a cycle somehow reaches the database (a formula saved
 *      before validation existed, two concurrent writes to `ajustes`), the cell shows `#¡REF!`
 *      instead of overflowing the stack and taking the whole page down.
 *
 *   3. A column with a formula is NOT editable. Hand-writing over a computed value is a separate
 *      feature (the per-cell manual override) with its own persistence and audit trail.
 */

import type { GridColumn } from '@/shared/components/Grid/types'
import {
  DEFAULT_FORMAT,
  EMPTY_FORMULA_CONFIG,
  formulaField,
  formulaIdOf,
  isFormulaField,
  type FormulaColumn,
  type GridFormulaConfig,
} from './config'

// The configuration shape moved to `./config`, which knows nothing about the
// grid; it is re-exported here so callers need not care where it lives.
export {
  DEFAULT_FORMAT,
  EMPTY_FORMULA_CONFIG,
  formulaField,
  formulaIdOf,
  isFormulaField,
  type FormulaColumn,
  type GridFormulaConfig,
}
import { formatearExcel } from './excelFormat'
import {
  columnScope,
  evaluate,
  formulaReferences,
  fromCell,
  localizeErrorCode,
  producesText,
  type AstNode,
  type ColumnScope,
  type FormulaValue,
} from './language'
import { fromStored, type StoredFormula } from './stored'


// ── Types ─────────────────────────────────────────────────────────────────────────────────────

/** A calculated column: one that does not exist in the page's code, only in `ajustes`. */
/**
 * A grid's formulas: the calculated columns it adds, plus formulas that
 * override an existing base column.
 *
 * TMS kept this type in its persistence module. Here it lives beside the engine
 * because persistence is the part that changes — one row per organization
 * instead of a single-tenant `ajustes` table — while the shape the engine
 * evaluates does not.
 */


export interface FormulaProblem {
  /** Effective field of the offending column: `calc:${id}` or a base column's field. */
  field: string
  kind: 'syntax' | 'cycle' | 'missing-ref'
  /** Spanish, ready to show. */
  message: string
  /** `syntax` only: the range to underline in the editor. */
  start?: number
  end?: number
}

export interface FormulaEngine<T> {
  /** The EFFECTIVE column list: the page's own columns, with overridden ones rewired to their
   *  formula, plus the calculated columns appended. */
  columns: GridColumn<T>[]
  /** Typed value of a formula column for one row — for tests and for the editor's preview. */
  valueOf: (field: string, row: T) => FormulaValue
  /** Configuration errors to surface: syntax, cycles, dead references. */
  problems: FormulaProblem[]
  /** Fields in an allowed cycle → their group. These stay typeable. */
  cycleGroups: ReadonlyMap<string, readonly string[]>
  /** Whether `field` is this row's input for its cycle, so it shows what is stored. */
  isCycleInput: (field: string, row: T) => boolean
  /** Clears the per-row memo. Call it when `rows` changes identity, and after any edit that
   *  mutates a row object IN PLACE instead of replacing it. */
  invalidate: () => void
}


/** With an empty pattern `formatearExcel` falls back to `String(n)` and would print `1.5` with a
 *  dot decimal, inconsistent with the rest of the app. Written in CANONICAL convention (`.`
 *  decimal, `,` thousands), which is how patterns are stored; with the default separator it
 *  renders as `1.234,50`. */

// ── Parse cache ───────────────────────────────────────────────────────────────────────────────

/**
 * Decoding replaced parsing when storage became structural, so the old text-keyed parse cache is
 * gone with it: `fromStored` is an object walk with no lexing, which is what the cache existed to
 * avoid. A formula that cannot be decoded comes back as a `raw` node — never an exception — and
 * `collectProblems` is what turns that into something the user sees.
 */
const compile = (formula: StoredFormula): AstNode => fromStored(formula)

/** The fields a decoded formula depends on inside THIS document. A cross-document reference has
 *  no local dependency, so it never joins the graph. */
const localRefs = (ast: AstNode): string[] =>
  formulaReferences(ast).filter((ref) => ref.documentId === undefined).map((ref) => ref.field)

/** The fields a formula depends on in THIS sheet — the only ones whose existence can be checked
 *  against the grid's own columns. A reference into another sheet or document is resolved by the
 *  cell runtime instead, and naming a column this grid does not have is exactly the point of it. */
const sameSheetRefs = (ast: AstNode): string[] =>
  formulaReferences(ast)
    .filter((ref) => ref.documentId === undefined && ref.sheetId === undefined)
    .map((ref) => ref.field)

// ── Formula set ───────────────────────────────────────────────────────────────────────────────

/** Every formula of a grid, flattened to `field → stored formula`, whichever kind it is. */
export function formulasByField(config: GridFormulaConfig): Map<string, StoredFormula> {
  const byField = new Map<string, StoredFormula>()
  for (const column of config.columns) byField.set(formulaField(column.id), column.formula)
  for (const [field, formula] of Object.entries(config.overrides)) byField.set(field, formula)
  return byField
}

// ── Dependency graph ──────────────────────────────────────────────────────────────────────────

/** Field → the fields it depends on that ALSO carry a formula (the only ones that can cycle). */
export function dependencyGraph(config: GridFormulaConfig): Map<string, string[]> {
  const byField = formulasByField(config)
  const graph = new Map<string, string[]>()
  for (const [field, formula] of byField) {
    graph.set(field, localRefs(compile(formula)).filter((ref) => byField.has(ref)))
  }
  return graph
}

/**
 * Evaluation order, or the actual cycle. Three-colour DFS rather than Kahn's algorithm because
 * the error message has to name the cycle, not just report that one exists.
 */
export function topologicalOrder(
  graph: Map<string, string[]>,
): { ok: true; order: string[] } | { ok: false; cycle: string[] } {
  const state = new Map<string, 'visiting' | 'done'>()
  const order: string[] = []
  const stack: string[] = []
  let cycle: string[] | null = null

  const visit = (field: string): void => {
    if (cycle !== null || state.get(field) === 'done') return
    if (state.get(field) === 'visiting') {
      // Report the path from where the cycle started, closed back onto itself.
      cycle = [...stack.slice(stack.indexOf(field)), field]
      return
    }
    state.set(field, 'visiting')
    stack.push(field)
    for (const dep of graph.get(field) ?? []) visit(dep)
    stack.pop()
    state.set(field, 'done')
    order.push(field)
  }

  for (const field of graph.keys()) visit(field)
  return cycle !== null ? { ok: false, cycle } : { ok: true, order }
}

// ── Cycles between typeable columns ──────────────────────────────────────────────────────────

/** Row key holding when a cycle member was last typed into (epoch ms). */
export const EDITED_AT_PREFIX = '__edited_at:'
export const editedAtField = (field: string): string => `${EDITED_AT_PREFIX}${field}`

/**
 * Formula fields that reference each other, as strongly connected groups (Tarjan). A field that
 * only references itself is a group of one; a field in no cycle is in no group.
 */
export function formulaCycles(config: GridFormulaConfig): string[][] {
  const graph = dependencyGraph(config)
  const index = new Map<string, number>()
  const low = new Map<string, number>()
  const onStack = new Set<string>()
  const stack: string[] = []
  const groups: string[][] = []
  let next = 0
  const visit = (field: string): void => {
    index.set(field, next)
    low.set(field, next)
    next++
    stack.push(field)
    onStack.add(field)
    for (const dep of graph.get(field) ?? []) {
      if (!index.has(dep)) {
        visit(dep)
        low.set(field, Math.min(low.get(field)!, low.get(dep)!))
      } else if (onStack.has(dep)) {
        low.set(field, Math.min(low.get(field)!, index.get(dep)!))
      }
    }
    if (low.get(field) !== index.get(field)) return
    const group: string[] = []
    let member: string
    do {
      member = stack.pop()!
      onStack.delete(member)
      group.push(member)
    } while (member !== field)
    if (group.length > 1 || (graph.get(field) ?? []).includes(field)) groups.push(group.reverse())
  }
  for (const field of graph.keys()) if (!index.has(field)) visit(field)
  return groups
}

/**
 * A cycle is allowed when something typed can always break it: two or more stored columns, none
 * of them a calculated column (which stores nothing). A column that uses only itself is never
 * allowed. In each row, the member typed into last is the input and the rest compute from it.
 */
export function cycleGroups(config: GridFormulaConfig): Map<string, readonly string[]> {
  const byField = new Map<string, readonly string[]>()
  for (const group of formulaCycles(config)) {
    if (group.length < 2 || group.some((field) => formulaIdOf(field) !== null)) continue
    for (const field of group) byField.set(field, group)
  }
  return byField
}

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === ''

/**
 * The member of `group` that is this row's input: the one typed into last; failing that (rows
 * from before the cycle existed) the first with something stored; failing that the first one.
 */
export function cycleInput(
  group: readonly string[],
  row: Readonly<Record<string, unknown>>,
  stored: (field: string) => unknown,
): string {
  let latest: string | undefined
  let latestAt = -Infinity
  for (const field of group) {
    const at = row[editedAtField(field)]
    if (typeof at === 'number' && at > latestAt) {
      latest = field
      latestAt = at
    }
  }
  return latest ?? group.find((field) => !isBlank(stored(field))) ?? group[0]!
}

// ── Scope ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Maps visible names to fields over the grid's real columns plus its calculated ones. The visible
 * name follows the same rule as `headerOf` in the Grid — the shared rename override wins over the
 * column's own `headerName` — so the user writes the name they actually SEE.
 */
export function gridScope<T>(
  baseColumns: readonly GridColumn<T>[],
  config: GridFormulaConfig,
  headerOverrides: Readonly<Record<string, string>>,
): ColumnScope {
  const entries: { field: string; name: string }[] = []
  for (const column of baseColumns) {
    // Columns without a `field` are keyed by position, which is not stable, so they cannot be
    // referenced. In practice those are the action columns.
    if (column.field === undefined) continue
    entries.push({ field: column.field, name: headerOverrides[column.field] ?? column.headerName })
  }
  for (const column of config.columns) {
    const field = formulaField(column.id)
    entries.push({ field, name: headerOverrides[field] ?? column.title })
  }
  return columnScope(entries)
}

// ── Problems ──────────────────────────────────────────────────────────────────────────────────

function collectProblems(config: GridFormulaConfig, scope: ColumnScope): FormulaProblem[] {
  const problems: FormulaProblem[] = []
  for (const [field, formula] of formulasByField(config)) {
    const ast = compile(formula)
    // A `raw` node is a formula that never parsed — either saved that way on purpose so the user
    // could come back to it, or stored by a version this one cannot read.
    if (ast.kind === 'raw') {
      problems.push({
        field,
        kind: 'syntax',
        message: 'La fórmula tiene un error de sintaxis.',
        start: 0,
        end: ast.text.length,
      })
      continue
    }
    for (const ref of sameSheetRefs(ast)) {
      if (scope.nameOfField(ref) === undefined) {
        problems.push({ field, kind: 'missing-ref', message: `La columna «${ref}» ya no existe en esta planilla.` })
      }
    }
  }

  const allowed = cycleGroups(config)
  for (const cycle of formulaCycles(config)) {
    if (allowed.has(cycle[0]!)) continue
    const named = [...cycle, cycle[0]!].map((field) => scope.nameOfField(field) ?? field)
    problems.push({
      field: cycle[0] ?? '',
      kind: 'cycle',
      message: `Referencia circular: ${named.map((n) => `«${n}»`).join(' → ')}. Solo las columnas que se pueden escribir pueden depender entre sí; una columna calculada o una que se usa a sí misma, no.`,
    })
  }
  return problems
}

/**
 * Checks the state the grid WOULD have after saving: syntax, references to columns that do not
 * exist, and circular references. Returns the first problem, or `null` when it is safe to save.
 * A cycle is never persisted.
 */
export function validateFormulas<T>(
  config: GridFormulaConfig,
  field: string,
  baseColumns: readonly GridColumn<T>[],
  headerOverrides: Readonly<Record<string, string>>,
): FormulaProblem | null {
  const scope = gridScope(baseColumns, config, headerOverrides)
  const problems = collectProblems(config, scope)
  // Report the edited column's own problem first; a cycle it took part in counts as its own.
  return problems.find((p) => p.field === field) ?? problems.find((p) => p.kind === 'cycle') ?? null
}

// ── Engine ────────────────────────────────────────────────────────────────────────────────────

export function createFormulaEngine<T extends object>(
  baseColumns: readonly GridColumn<T>[],
  config: GridFormulaConfig,
  headerOverrides: Readonly<Record<string, string>> = {},
  locale: string = 'en',
): FormulaEngine<T> {
  const scope = gridScope(baseColumns, config, headerOverrides)
  const byField = formulasByField(config)

  // Decoded once per formula, so the per-row work is only an AST walk.
  const programs = new Map<string, AstNode>()
  for (const [field, formula] of byField) programs.set(field, compile(formula))

  const baseByField = new Map<string, GridColumn<T>>()
  for (const column of baseColumns) {
    // First field wins: `clavesColumnas` in the Grid already documents that duplicate fields
    // happen (several action columns over `id`), and those never carry a numeric meaning.
    if (column.field !== undefined && !baseByField.has(column.field)) baseByField.set(column.field, column)
  }

  // Per-row memo, so a formula column referenced by two others is evaluated once per row. A
  // WeakMap so discarded rows are not retained.
  let memo = new WeakMap<T, Map<string, FormulaValue>>()

  // Mirrors `readValue` in Grid.tsx. Deliberately duplicated rather than imported: Grid.tsx
  // imports this module, so taking a runtime value from it would close an import cycle.
  const readBase = (column: GridColumn<T>, row: T): unknown => {
    if (column.valueGetter) return column.valueGetter({ data: row })
    if (column.field) return (row as Record<string, unknown>)[column.field]
    return undefined
  }

  const groups = cycleGroups(config)
  const isCycleInput = (field: string, row: T): boolean => {
    const group = groups.get(field)
    if (group === undefined) return false
    const stored = (member: string): unknown => {
      const column = baseByField.get(member)
      return column === undefined ? undefined : readBase(column, row)
    }
    return cycleInput(group, row as Record<string, unknown>, stored) === field
  }

  function valueOf(field: string, row: T, inProgress: Set<string> = new Set()): FormulaValue {
    const program = programs.get(field)

    if (program === undefined || isCycleInput(field, row)) {
      // No formula on this field: read the underlying cell.
      const column = baseByField.get(field)
      return column === undefined ? { kind: 'error', code: '#¡REF!' } : fromCell(readBase(column, row))
    }
    // A formula that never parsed shows #¡NOMBRE? in every row: the column still exists and the
    // page still renders, which is the point.
    if (program.kind === 'raw') return { kind: 'error', code: '#¡NOMBRE?' }
    if (inProgress.has(field)) return { kind: 'error', code: '#¡REF!' }

    const cached = memo.get(row)?.get(field)
    if (cached !== undefined) return cached

    inProgress.add(field)
    const result = evaluate(program, (ref) => valueOf(ref, row, inProgress))
    inProgress.delete(field)

    const forRow = memo.get(row) ?? new Map<string, FormulaValue>()
    forRow.set(field, result)
    memo.set(row, forRow)
    return result
  }

  /** Primitives only — see note 1 at the top of this module. */
  const getterFor = (field: string) => ({ data }: { data?: T }): unknown => {
    if (!data) return null
    const value = valueOf(field, data)
    switch (value.kind) {
      case 'number':
        return value.n
      case 'text':
        return value.s
      case 'error':
        return localizeErrorCode(value.code, locale)
      case 'empty':
        return null
    }
  }

  /** An error code is a string, so any numeric formatter has to be kept away from it. */
  const formatterFor = (pattern: string) => ({ value }: { value: unknown }): string =>
    typeof value === 'number' ? formatearExcel(value, pattern) : typeof value === 'string' ? value : ''

  /** A formula that yields text is left-aligned and keeps its `CHAR(10)` line breaks; one that
   *  yields a number keeps the numeric alignment every other amount column has. */
  const textColumn = (field: string): boolean => {
    const program = programs.get(field)
    return program !== undefined && producesText(program)
  }

  const overridden: GridColumn<T>[] = baseColumns.map((column) => {
    if (column.field === undefined || !programs.has(column.field)) return column
    const base = column.valueFormatter
    // A formula replaces where the value comes from, so the column's own
    // editor, renderer and dropdown are dropped rather than overwritten.
    const { cellEditor: _editor, cellRenderer: _renderer, dropdown: _dropdown, ...rest } = column
    return {
      ...rest,
      valueGetter: getterFor(column.field),
      // Keep the column's own formatting (a `total` column stays currency-formatted), but never
      // hand it an error code.
      valueFormatter: base
        ? (params) => (typeof params.value === 'number' ? base(params) : typeof params.value === 'string' ? params.value : '')
        : formatterFor(DEFAULT_FORMAT),
      // The value no longer comes from the row, so the inline editor must not offer to change it
      // (see note 3). A column may explicitly retain a renderer that is based on the computed
      // value (for example, negative stock in red); form controls remain disabled by default.
      // A member of an allowed cycle stays typeable: typing makes it the row's input.
      editable: groups.has(column.field) ? column.editable ?? true : false,
      // The keys are OMITTED, not set to `undefined`: under
      // `exactOptionalPropertyTypes` an explicit undefined is a different type
      // from an absent optional key, and `rest` already dropped them.
      ...(column.preserveRendererWithFormula && column.cellRenderer !== undefined
        ? { cellRenderer: column.cellRenderer }
        : {}),
      cellType: textColumn(column.field) ? 'text' : 'number',
      multiline: textColumn(column.field) || column.multiline === true,
    }
  })

  const calculated: GridColumn<T>[] = config.columns.map((column) => {
    const field = formulaField(column.id)
    return {
      field,
      headerName: column.title,
      cellType: textColumn(field) ? 'text' : 'number',
      multiline: textColumn(field),
      minWidth: column.minWidth ?? 110,
      valueGetter: getterFor(field),
      valueFormatter: formatterFor(column.format ?? DEFAULT_FORMAT),
    }
  })

  return {
    columns: [...overridden, ...calculated],
    valueOf: (field, row) => valueOf(field, row),
    problems: collectProblems(config, scope),
    cycleGroups: groups,
    isCycleInput,
    invalidate: () => {
      memo = new WeakMap()
    },
  }
}
